/**
 * Environment-specific Sentry configuration
 * 
 * This file provides full Sentry functionality for both development and production.
 * Development gets enhanced logging for debugging while maintaining the same
 * data collection as production.
 */

import * as Sentry from "@sentry/nextjs";

/**
 * Development configuration - Smart CORS handling with full logging
 * Provides complete development debugging while gracefully handling CORS issues
 */
export const developmentConfig = {
  // Enhanced event handling for development with better CORS handling
  beforeSend(event: any, hint: any) {
    // Always log ALL events for development debugging (regardless of sending)
    console.log('🔍 Sentry Event (Development):', {
      id: event.event_id,
      type: event.type,
      message: event.message || event.exception?.[0]?.value,
      level: event.level,
      timestamp: event.timestamp,
      tags: event.tags,
      user: event.user,
      breadcrumbs: event.breadcrumbs?.length || 0
    });

    // Send all errors and warnings (should work with new CORS config)
    if (event.level === 'error' || event.level === 'fatal' || event.level === 'warning') {
      console.log('✅ Sending event to Sentry:', event.event_id, event.level);
      return event;
    }

    // Send info messages from explicit Sentry calls (testing)
    if (event.level === 'info' && event.type !== 'log') {
      console.log('✅ Sending info event to Sentry:', event.event_id);
      return event;
    }

    // Filter out noisy console logs but allow other events
    if (event.type === 'log' || event.logger === 'console') {
      console.log('📝 Console log captured (local only):', event.message);
      return null;
    }

    // Send other events (transactions, etc.) - try with new CORS setup
    console.log('✅ Sending other event to Sentry:', event.type, event.level);
    return event;
  },

  beforeSendTransaction(event: any) {
    // Log all transactions for debugging and try sending with new CORS config
    console.log('🚀 Transaction captured:', {
      transaction: event.transaction,
      id: event.event_id,
      duration: event.timestamp,
      spans: event.spans?.length || 0
    });

    // Try sending transactions now that we have better CORS configuration
    console.log('✅ Sending transaction to Sentry:', event.event_id);
    return event;
  },

  // Disable replay in development to avoid CORS issues
  replay: {
    enabled: false,
    maskAllText: false,
    blockAllMedia: false,
  },

  // Higher sample rate for development (more data)
  tracesSampleRate: 1.0,
};

/**
 * Production-safe Sentry configuration
 * Full functionality with no filtering
 */
export const productionConfig = {
  // Send all events in production
  beforeSend(event: any) {
    return event;
  },

  beforeSendTransaction(event: any) {
    return event;
  },

  // Enable replay in production
  replay: {
    enabled: true,
    maskAllText: true,
    blockAllMedia: true,
  },

  // Normal sample rate for production
  tracesSampleRate: 0.1,
};

/**
 * Get the appropriate configuration based on environment
 */
export function getSentryConfig() {
  if (process.env.NODE_ENV === 'development') {
    return developmentConfig;
  }
  return productionConfig;
}
